import { parseColumnKind, ColumnKind } from "./datasetColumnMapping";

// Standard time-series CSV (docs/architecture/STANDARD_TIMESERIES_CSV.md): parser (§8) and the adapter that turns a
// parsed file into the ProcessedModel forecast JSON shape the existing graphs read. Minimal first cut of #841.

export interface ParameterInfo {
  unit?: string;
  name?: string;
  description?: string;
  kind?: "forecast" | "observation" | "derived";
  columns: number[];
  extra: Record<string, string>;
  /** `qc_codes` row: bit value → label for the parameter's `qc_` column */
  qcCodes?: Record<string, string>;
}

export interface ColumnDescriptor {
  name: string;
  parameter: string;
  kind: ColumnKind;
  percentile?: number;
  member?: number;
}

export interface StandardTimeSeries {
  metadata: Record<string, string>;
  parameters: Record<string, ParameterInfo>;
  columns: ColumnDescriptor[];
  rows: { t: string; values: (number | string | null)[] }[];
}

/** Percentiles computed from `m_NN_` members when a file carries no `p_NN_` columns (spec §7 default). */
export const DEFAULT_MEMBER_PERCENTILES = [10, 25, 50, 75, 90];

const USER_TIME_HEADERS = new Set(["timestamp", "time", "date"]);

/** RFC 4180 rows: quoted cells may hold commas, doubled quotes and newlines. */
const parseCsvCells = (text: string): string[][] => {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = "";
  let quoted = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (quoted) {
      if (ch === '"' && text[i + 1] === '"') {
        cell += '"';
        i++;
      } else if (ch === '"') {
        quoted = false;
      } else {
        cell += ch;
      }
    } else if (ch === '"') {
      quoted = true;
    } else if (ch === ",") {
      row.push(cell);
      cell = "";
    } else if (ch === "\n" || ch === "\r") {
      if (ch === "\r" && text[i + 1] === "\n") i++;
      row.push(cell);
      rows.push(row);
      row = [];
      cell = "";
    } else {
      cell += ch;
    }
  }
  if (cell !== "" || row.length) {
    row.push(cell);
    rows.push(row);
  }
  return rows.filter((r) => r.some((c) => c.trim() !== ""));
};

const isHeaderRow = (row: string[]): boolean => {
  const first = (row[0] ?? "").trim().toLowerCase();
  return first.includes("datetime") || USER_TIME_HEADERS.has(first);
};

/** ISO instant with a `Z` suffix; `+00:00` and a missing suffix are read as UTC (spec §6). */
const toIsoZ = (raw: string): string => {
  const text = raw.trim();
  const hasZone = /(?:z|[+-]\d{2}:?\d{2})$/i.test(text);
  const date = new Date(hasZone ? text : `${text}Z`);
  return Number.isNaN(date.valueOf()) ? text : date.toISOString().replace(".000Z", "Z");
};

const toCellValue = (raw: string): number | string | null => {
  const text = raw.trim();
  if (text === "") return null;
  const num = Number(text);
  return Number.isNaN(num) ? text : num;
};

const describeColumn = (name: string): ColumnDescriptor => {
  const { kind, parameter } = parseColumnKind(name);
  const descriptor: ColumnDescriptor = { name, parameter: parameter ?? "value", kind };
  if (kind === "percentile") descriptor.percentile = parseInt(/^p_?(\d{1,3})/i.exec(name)?.[1] ?? "", 10);
  if (kind === "member") descriptor.member = parseInt(/^m_?(\d{1,3})/i.exec(name)?.[1] ?? "", 10);
  return descriptor;
};

/** `qc_codes` cell: a JSON object of bit value → label; null when it isn't one. */
const parseQcCodes = (raw: string): Record<string, string> | null => {
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return null;
    return Object.fromEntries(Object.entries(parsed).map(([bit, label]) => [bit, String(label)]));
  } catch {
    return null;
  }
};

/** Parse a Standard time-series CSV. Throws when no header row is found. */
export const parseStandardTimeSeriesCsv = (text: string): StandardTimeSeries => {
  const rows = parseCsvCells(text);
  const headerIndex = rows.findIndex(isHeaderRow);
  if (headerIndex === -1) throw new Error("Standard time-series CSV has no DateTime header row");

  const metadata: Record<string, string> = {};
  const parameters: Record<string, ParameterInfo> = {};
  const parameterFor = (slug: string): ParameterInfo => {
    const key = slug.toLowerCase();
    if (!parameters[key]) parameters[key] = { columns: [], extra: {} };
    return parameters[key];
  };

  rows.slice(0, headerIndex).forEach((row) => {
    const [key, second, third] = row.map((c) => c.trim());
    if (!key) return;
    if (row.length === 2) {
      metadata[key] = second;
    } else if (row.length >= 3) {
      const info = parameterFor(second);
      switch (key) {
        case "unit":
        case "name":
        case "description":
          info[key] = third;
          break;
        case "qc_codes": {
          const codes = parseQcCodes(third);
          if (codes) info.qcCodes = codes;
          else info.extra[key] = third;
          break;
        }
        case "kind":
          if (third === "forecast" || third === "observation" || third === "derived") info.kind = third;
          else info.extra[key] = third;
          break;
        default:
          info.extra[key] = third;
      }
    }
  });

  const columns = rows[headerIndex].slice(1).map((name) => describeColumn(name.trim()));
  columns.forEach((column, index) => parameterFor(column.parameter).columns.push(index));

  const dataRows = rows.slice(headerIndex + 1).map((row) => ({
    t: toIsoZ(row[0] ?? ""),
    values: columns.map((_, i) => toCellValue(row[i + 1] ?? "")),
  }));

  return { metadata, parameters, columns, rows: dataRows };
};

/** numpy's default (linear) percentile over the non-null samples. */
const percentileOf = (samples: number[], p: number): number | null => {
  if (!samples.length) return null;
  const sorted = [...samples].sort((a, b) => a - b);
  const rank = (p / 100) * (sorted.length - 1);
  const lo = Math.floor(rank);
  const hi = Math.ceil(rank);
  return sorted[lo] + (sorted[hi] - sorted[lo]) * (rank - lo);
};

/** Indexes of a parameter's `m_NN_` member columns, parameter compared case-insensitively. */
const memberIndexesOf = (ts: StandardTimeSeries, parameter: string): number[] =>
  ts.columns.flatMap((column, index) =>
    column.kind === "member" && column.parameter.toLowerCase() === parameter.toLowerCase() ? [index] : []
  );

/** Numeric values of the given columns in one row; nulls are dropped. */
const memberSamples = (values: (number | string | null)[], indexes: number[]): number[] =>
  indexes.map((i) => values[i]).filter((v): v is number => typeof v === "number");

/** Series key the graphs understand for a non-member column: `p10` bands, `mean`, `control`, `value`, … */
const seriesKeyOf = (column: ColumnDescriptor): string =>
  column.kind === "percentile" ? `p${column.percentile}` : column.kind;

/**
 * Convert a parsed file to the ProcessedModel forecast JSON shape
 * `{forecast_issued, models: {<parameter>: {variation, yUnit, data: {ts: {series: value}}}}}`.
 * Members are reduced to percentiles on the fly unless the parameter already has `p_NN_` columns (spec §7);
 * the control and mean are never part of that sample.
 */
export const standardTimeSeriesToForecastPayload = (
  series: StandardTimeSeries,
  memberPercentiles: number[] = DEFAULT_MEMBER_PERCENTILES
): { forecast_issued?: string; models: Record<string, any> } => {
  const models: Record<string, any> = {};

  Object.values(series.parameters).forEach((info) => {
    if (!info.columns.length) return;
    const paramColumns = info.columns.map((index) => ({ index, column: series.columns[index] }));
    const percentileColumns = paramColumns
      .filter(({ column }) => column.kind === "percentile")
      .sort((a, b) => (a.column.percentile ?? 0) - (b.column.percentile ?? 0));
    const memberIndexes = paramColumns.filter(({ column }) => column.kind === "member").map(({ index }) => index);
    // qc_ columns are flags on the parameter's values, not a series the forecast graphs should draw
    const otherColumns = paramColumns.filter(
      ({ column }) => column.kind !== "percentile" && column.kind !== "member" && column.kind !== "qc"
    );
    const computeFromMembers = memberIndexes.length > 0 && percentileColumns.length === 0;

    const data: Record<string, Record<string, number | string | null>> = {};
    series.rows.forEach(({ t, values }) => {
      const point: Record<string, number | string | null> = {};
      if (computeFromMembers) {
        const samples = memberSamples(values, memberIndexes);
        memberPercentiles.forEach((p) => {
          point[`p${p}`] = percentileOf(samples, p);
        });
      } else {
        percentileColumns.forEach(({ index, column }) => {
          point[seriesKeyOf(column)] = values[index];
        });
      }
      otherColumns.forEach(({ index, column }) => {
        point[seriesKeyOf(column)] = values[index];
      });
      data[t] = point;
    });

    const parameter = series.columns[info.columns[0]].parameter;
    models[parameter] = {
      variation: info.name || parameter,
      graphType: "scatter",
      yUnit: info.unit || "",
      data,
    };
  });

  return { forecast_issued: series.metadata.forecast_issued, models };
};

const pad2 = (n: number): string => String(n).padStart(2, "0");

// Series key → column prefix, mirrors erode-models `_SERIES_PREFIX` exactly
const SERIES_PREFIX: Record<string, string> = {
  value: "",
  pred: "",
  predicted: "",
  mean: "mean_",
  actual: "obs_",
  obs: "obs_",
  observed: "obs_",
  median: "median_",
  min: "min_",
  max: "max_",
  std: "std_",
  control: "c_",
  c: "c_",
};

// Python's round(): half-to-even for the non-negative inputs used here, so 0.125 → 12
const roundHalfEven = (x: number): number => {
  const r = Math.round(x);
  return Math.abs(x % 1) === 0.5 && r % 2 !== 0 ? r - 1 : r;
};

// Python's float(): finite decimal/exponent forms only ("" and "0x1" are not numbers)
const parseFloatStrict = (s: string): number | null =>
  /^[+-]?(\d(_?\d)*\.?(\d(_?\d)*)?|\.\d(_?\d)*)(e[+-]?\d(_?\d)*)?$/.test(s) ? Number(s.replace(/_/g, "")) : null;

// Python's str.strip() set: unlike JS trim() it strips \x1c-\x1f and keeps the BOM
// eslint-disable-next-line no-control-regex
const PY_STRIP = /^[\t\n\v\f\r \x1c-\x1f\x85\xa0\u1680\u2000-\u200a\u2028\u2029\u202f\u205f\u3000]+|[\t\n\v\f\r \x1c-\x1f\x85\xa0\u1680\u2000-\u200a\u2028\u2029\u202f\u205f\u3000]+$/g;
const pyStrip = (s: string): string => s.replace(PY_STRIP, "");

/** Port of `_percentile` (key already stripped + lowercased): "0.10" → 10, "p50" → 50, "q0.9" → 90, "50" → 50, bare "1" → 100; else null. */
const percentileOfKey = (key: string): number | null => {
  let k = key;
  if (k[0] === "p" || k[0] === "q") {
    k = k.slice(1);
    if (/^\d+$/.test(k)) {
      const n = parseInt(k, 10);
      return n <= 100 ? n : null;
    }
  }
  const x = parseFloatStrict(k);
  if (x === null) return null;
  if (x >= 0 && x <= 1) return roundHalfEven(x * 100);
  if (x > 1 && x <= 100) return roundHalfEven(x);
  return null;
};

/** Port of `_slug`: "WindPowerForecast" → "wind_power_forecast". */
const slug = (s: string): string =>
  s
    .replace(/([a-z0-9])(?=[A-Z])/g, "$1_")
    .replace(/[^A-Za-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .toLowerCase();

/** Header column for a JSON series key (port of erode-models `csv_format.column_name`): "0.50" → `p_50_<param>`. */
export const columnName = (seriesKey: string, parameter: string): string => {
  const key = pyStrip(seriesKey);
  const low = key.toLowerCase();
  if (Object.prototype.hasOwnProperty.call(SERIES_PREFIX, low)) return `${SERIES_PREFIX[low]}${parameter}`;
  const pct = percentileOfKey(low);
  if (pct !== null) return `p_${pad2(pct)}_${parameter}`;
  const member = /^m[_-]?(\d{1,3})$/.exec(low);
  if (member) return `m_${pad2(parseInt(member[1], 10))}_${parameter}`;
  return `${parameter}_${slug(key)}`;
};

/** Columns grouped by parameter, compared case-insensitively (spec §5.2); keys are lowercased parameter names. */
export const groupByParameter = (ts: StandardTimeSeries): Record<string, ColumnDescriptor[]> => {
  const groups: Record<string, ColumnDescriptor[]> = {};
  ts.columns.forEach((column) => {
    const key = column.parameter.toLowerCase();
    if (!groups[key]) groups[key] = [];
    groups[key].push(column);
  });
  return groups;
};

/** Per-row percentiles over a parameter's `m_NN_` members only; control and mean are excluded (spec §7). */
export const percentilesFromMembers = (
  ts: StandardTimeSeries,
  parameter: string,
  percentiles: number[] = DEFAULT_MEMBER_PERCENTILES
): { t: string; values: Record<number, number | null> }[] => {
  const memberIndexes = memberIndexesOf(ts, parameter);
  return ts.rows.map(({ t, values }) => {
    const samples = memberSamples(values, memberIndexes);
    return { t, values: Object.fromEntries(percentiles.map((p) => [p, percentileOf(samples, p)])) };
  });
};
